/**
 * Sidebar — collapsible sidebar with browser-local chats and free usage status.
 * Uses @tanstack/react-virtual for virtualized rendering and tier-grouped display.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { AllowanceState, ChatListItem } from '../../types';
import type { ConnectionStatus } from '../../hooks/useConnection';
import type { InfoVariant } from '../modals/InfoModal';
import styles from '../../styles/components/Sidebar.module.css';

interface SidebarProps {
  chats: ChatListItem[];
  currentChatId: string | null;
  connectionStatus: ConnectionStatus;
  isLoadingChats?: boolean;
  isBusy?: boolean;
  allowance?: AllowanceState | null;
  onNewChat: () => void;
  onLoadChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onPinChat?: (chatId: string) => void;
  onExportChat?: (chatId: string) => void;
  onShowInfo?: (variant: InfoVariant) => void;
  onOpenQwenRoom?: () => void;
  onOpenMainChat?: () => void;
  qwenRoomActive?: boolean;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function formatResetDuration(seconds: number): string {
  if (seconds <= 0) return 'Next free message available soon.';

  const totalMinutes = Math.max(1, Math.ceil(seconds / 60));
  if (totalMinutes < 60) return `Next free message in ${totalMinutes}m.`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `Next free message in ${hours}h${minutes ? ` ${minutes}m` : ''}.`;
}

export function AllowanceStatus({ allowance }: { allowance?: AllowanceState | null }) {
  if (!allowance) return null;

  const exhausted = allowance.remaining === 0;
  const resetSeconds = Math.max(
    allowance.next_reset_in_seconds,
    allowance.retry_after_seconds
  );

  return (
    <div
      className={`${styles.allowanceStatus} ${exhausted ? styles.allowanceExhausted : ''}`}
      aria-live="polite"
    >
      <div className={styles.allowanceCount}>
        Free messages: {allowance.remaining} / {allowance.limit}
      </div>
      {exhausted && (
        <div className={styles.allowanceReset}>{formatResetDuration(resetSeconds)}</div>
      )}
    </div>
  );
}

export function ConnectionStatusIndicator({ status }: { status: ConnectionStatus }) {
  const checking = status.lastChecked === null;
  const label = status.connected
    ? `Connected${status.model ? ` — ${status.model}` : ''}`
    : checking
      ? 'Connecting…'
      : 'Disconnected';
  const stateClass = status.connected
    ? styles.connected
    : checking
      ? styles.checking
      : styles.disconnected;

  return (
    <div className={styles.statusRow} role="status" aria-live="polite">
      <span className={`${styles.statusDot} ${stateClass}`} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Sidebar({
  chats,
  currentChatId,
  connectionStatus,
  isLoadingChats,
  isBusy,
  allowance,
  onNewChat,
  onLoadChat,
  onDeleteChat,
  onPinChat,
  onExportChat,
  onShowInfo,
  onOpenQwenRoom,
  onOpenMainChat,
  qwenRoomActive,
}: SidebarProps) {
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  // Sort: pinned first, then by lastUpdated descending
  const sortedChats = useMemo(() => {
    return [...chats].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      const aUpdated = Number(a.lastUpdated ?? a.createdAt ?? 0);
      const bUpdated = Number(b.lastUpdated ?? b.createdAt ?? 0);
      if (bUpdated !== aUpdated) return bUpdated - aUpdated;
      const aCreated = Number(a.createdAt ?? 0);
      const bCreated = Number(b.createdAt ?? 0);
      if (bCreated !== aCreated) return bCreated - aCreated;
      return String(b.chatId).localeCompare(String(a.chatId));
    });
  }, [chats]);

  // Split into Recent / Archived tiers
  const { recentChats, archivedChats } = useMemo(() => {
    const now = Date.now();
    const recent: ChatListItem[] = [];
    const archived: ChatListItem[] = [];
    for (const c of sortedChats) {
      const isRecent =
        c.pinned ||
        (!c.archived && Number(c.lastUpdated ?? 0) > now - SEVEN_DAYS_MS);
      if (isRecent) {
        recent.push(c);
      } else {
        archived.push(c);
      }
    }
    return { recentChats: recent, archivedChats: archived };
  }, [sortedChats]);

  // Build a flat virtual list: [recent items] + optional [archived header + archived items]
  type VirtualRow =
    | { type: 'chat'; chat: ChatListItem }
    | { type: 'header'; label: string; count: number };

  const rows = useMemo<VirtualRow[]>(() => {
    const result: VirtualRow[] = recentChats.map((c) => ({
      type: 'chat' as const,
      chat: c,
    }));
    if (archivedChats.length > 0) {
      result.push({
        type: 'header' as const,
        label: 'Archived',
        count: archivedChats.length,
      });
      if (archivedExpanded) {
        for (const c of archivedChats) {
          result.push({ type: 'chat' as const, chat: c });
        }
      }
    }
    return result;
  }, [recentChats, archivedChats, archivedExpanded]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (rows[index]?.type === 'header' ? 32 : 56),
    overscan: 5,
  });

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <span className={styles.title}>W AI</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onShowInfo && (
            <button
              className={styles.chatActionBtn}
              onClick={() => onShowInfo('about')}
              title="About W AI"
              style={{ fontSize: '0.75rem' }}
            >
              About
            </button>
          )}
          <button className={styles.newChatBtn} onClick={onNewChat} disabled={isBusy}>
            New Chat
          </button>
        </div>
      </div>

      {onOpenQwenRoom && (
        <nav className={styles.roomNav} aria-label="W AI sections">
          <button type="button" className={!qwenRoomActive ? styles.roomActive : ''} aria-current={!qwenRoomActive ? 'page' : undefined} onClick={onOpenMainChat}>
            <span>W AI chat</span>
          </button>
          <button type="button" className={qwenRoomActive ? styles.roomActive : ''} aria-current={qwenRoomActive ? 'page' : undefined} onClick={onOpenQwenRoom}>
            <span>Qwen Room</span><small>Evil × Sarcastic</small>
          </button>
        </nav>
      )}
      <div className={styles.chatList} ref={parentRef}>
        {isLoadingChats && (
          <div className={styles.loadingBar}>
            <div className={styles.loadingBarFill} />
          </div>
        )}
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) return null;

            if (row.type === 'header') {
              return (
                <div
                  key="archived-header"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <button
                    className={styles.sectionHeader}
                    onClick={() => setArchivedExpanded((prev) => !prev)}
                  >
                    <span>{archivedExpanded ? '\u25BC' : '\u25B6'}</span>
                    <span>Archived ({row.count})</span>
                  </button>
                </div>
              );
            }

            return (
              <div
                key={row.chat.chatId}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <ChatItem
                  chat={row.chat}
                  isActive={row.chat.chatId === currentChatId}
                  disabled={!!isBusy}
                  onLoad={onLoadChat}
                  onDelete={onDeleteChat}
                  onPin={onPinChat}
                  onExport={onExportChat}
                />
              </div>
            );
          })}
        </div>
        {!isLoadingChats && chats.length === 0 && (
          <div style={{ padding: '16px', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            No chats yet
          </div>
        )}
      </div>

      <div className={styles.statusPanel}>
        {qwenRoomActive ? (
          <>
            <div className={styles.chatCount}>One Akash GPU · two personalities</div>
            <div className={styles.chatCount}>30-second pauses · fresh context every 50 messages</div>
          </>
        ) : <>
        <AllowanceStatus allowance={allowance} />
        <ConnectionStatusIndicator status={connectionStatus} />

        {/* Infrastructure badges */}
        {connectionStatus.connected && onShowInfo && (
          <div style={{
            display: 'flex',
            gap: '6px',
            padding: '4px 0',
            flexWrap: 'wrap',
          }}>
            <button
              className={styles.chatActionBtn}
              onClick={() => onShowInfo('icp')}
              title="Internet Computer"
              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
            >
              <span style={{ color: '#69db7c', fontSize: '8px', marginRight: '3px' }}>●</span>ICP
            </button>
            <button
              className={styles.chatActionBtn}
              onClick={() => onShowInfo('akash')}
              title="Akash Network"
              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
            >
              <span style={{ color: connectionStatus.connected ? '#69db7c' : '#ff6b6b', fontSize: '8px', marginRight: '3px' }}>{connectionStatus.connected ? '●' : '○'}</span>Akash
            </button>
            <button
              className={styles.chatActionBtn}
              onClick={() => onShowInfo('storage')}
              title="Browser Storage"
              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
            >
              <span style={{ color: '#888', fontSize: '8px', marginRight: '3px' }}>●</span>Storage
            </button>
            <button
              className={styles.chatActionBtn}
              onClick={() => onShowInfo('model')}
              title="AI Model"
              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
            >
              <span style={{ color: connectionStatus.connected ? '#a78bfa' : '#888', fontSize: '8px', marginRight: '3px' }}>{connectionStatus.connected ? '●' : '○'}</span>Model
            </button>
          </div>
        )}

        <div className={styles.chatCount}>
          Free · 100 messages per 24 hours
        </div>
        <div className={styles.chatCount}>
          Chats stay in this tab. The daily limit is shared across tabs on this connection.
        </div>
        </>}
      </div>
    </div>
  );
}

function formatDate(timestamp: number): string {
  if (!timestamp) return '';
  // Backend stores timestamps in milliseconds — no need to multiply
  return new Date(timestamp).toLocaleDateString();
}

function ChatItem({
  chat,
  isActive,
  disabled,
  onLoad,
  onDelete,
  onPin,
  onExport,
}: {
  chat: ChatListItem;
  isActive: boolean;
  disabled: boolean;
  onLoad: (chatId: string) => void;
  onDelete: (chatId: string) => void;
  onPin?: (chatId: string) => void;
  onExport?: (chatId: string) => void;
}) {
  const handleLoad = useCallback(() => {
    if (disabled) return;
    onLoad(chat.chatId);
  }, [disabled, chat.chatId, onLoad]);
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onDelete(chat.chatId);
    },
    [disabled, chat.chatId, onDelete]
  );
  const handlePin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onPin?.(chat.chatId);
    },
    [disabled, chat.chatId, onPin]
  );
  const handleExport = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onExport?.(chat.chatId);
    },
    [disabled, chat.chatId, onExport]
  );

  return (
    <div
      className={`${styles.chatItem} ${isActive ? styles.chatItemActive : ''} ${chat.pinned ? styles.chatItemPinned : ''}`}
      onClick={handleLoad}
      style={disabled ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
    >
      <div className={styles.chatMeta}>
        <span className={styles.chatTitle}>
          {chat.pinned && <span className={styles.pinIcon}>*</span>}
          {chat.title || 'Untitled'}
        </span>
        {chat.lastUpdated > 0 && (
          <span className={styles.chatDate}>{formatDate(chat.lastUpdated)}</span>
        )}
      </div>
      <div className={styles.chatActions}>
        {onPin && (
          <button
            className={styles.chatActionBtn}
            onClick={handlePin}
            title={chat.pinned ? 'Unpin chat' : 'Pin chat'}
          >
            {chat.pinned ? '*' : '-'}
          </button>
        )}
        {onExport && (
          <button
            className={styles.chatActionBtn}
            onClick={handleExport}
            title="Export as Markdown"
          >
            &#8595;
          </button>
        )}
        <button
          className={`${styles.chatActionBtn} ${styles.deleteBtn}`}
          onClick={handleDelete}
          title="Delete chat"
        >
          &times;
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
